import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  FC,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { AppSettingsTabProps, TOnboardingConfig } from "../../models";

const CONSTANT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;
const INVALID_NAME_HELPER =
  "Constant name must start with a letter and use only letters, numbers, or underscores.";
const DUPLICATE_NAME_HELPER = "Constant name must be unique.";
const UNSUPPORTED_CONSTANT_HELPER =
  "Unsupported value preserved. Delete here or edit in Advanced tab.";

type ConstantsMap = NonNullable<TOnboardingConfig["constants"]>;

function isEditableConstantValue(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function sortConstantValues(values: string[]): string[] {
  return [...values].sort((left, right) => left.localeCompare(right));
}

function getSortedConstantValueEntries(
  value: string[],
): Array<{ index: number; item: string }> {
  return value
    .map((item, index) => ({ item, index }))
    .sort(
      (left, right) =>
        left.item.localeCompare(right.item) || left.index - right.index,
    );
}

function getNextConstantName(constants: ConstantsMap): string {
  let nextIndex = 1;

  while (
    Object.prototype.hasOwnProperty.call(constants, `newConstant${nextIndex}`)
  ) {
    nextIndex += 1;
  }

  return `newConstant${nextIndex}`;
}

function buildConfigWithConstants(
  prev: TOnboardingConfig,
  nextConstants: Record<string, unknown>,
): TOnboardingConfig {
  if (Object.keys(nextConstants).length === 0) {
    const { constants: _removed, ...rest } = prev;
    return rest;
  }

  return {
    ...prev,
    constants: nextConstants,
  };
}

function renameConstantKey(
  constants: ConstantsMap,
  currentName: string,
  nextName: string,
): ConstantsMap {
  return Object.fromEntries(
    Object.entries(constants).map(([name, value]) =>
      name === currentName ? [nextName, value] : [name, value],
    ),
  );
}

const ConstantRow: FC<{
  expanded: boolean;
  existingNames: string[];
  name: string;
  value: unknown;
  onAddValue: (name: string, value: string) => void;
  onDelete: (name: string) => void;
  onDeleteValue: (name: string, index: number) => void;
  onRename: (currentName: string, nextName: string) => void;
  onToggle: (name: string) => void;
}> = memo(
  ({
    expanded,
    existingNames,
    name,
    value,
    onAddValue,
    onDelete,
    onDeleteValue,
    onRename,
    onToggle,
  }) => {
    const [nameDraft, setNameDraft] = useState(name);
    const [nameError, setNameError] = useState<string | null>(null);
    const [valueDraft, setValueDraft] = useState("");
    const editableValue = useMemo(
      () => (isEditableConstantValue(value) ? value : null),
      [value],
    );
    const isEditable = editableValue !== null;
    const sortedEditableValue = useMemo(
      () => (editableValue ? getSortedConstantValueEntries(editableValue) : []),
      [editableValue],
    );
    const previewValues = useMemo(
      () => sortedEditableValue.slice(0, 2),
      [sortedEditableValue],
    );
    const overflowCount = sortedEditableValue.length - previewValues.length;

    useEffect(() => {
      setNameDraft(name);
      setNameError(null);
    }, [name]);

    const toggle = useCallback(() => {
      onToggle(name);
    }, [name, onToggle]);

    const stop = useCallback((event: ReactMouseEvent<HTMLElement>) => {
      event.stopPropagation();
    }, []);

    const commitName = useCallback(() => {
      if (!isEditable) {
        return;
      }

      const trimmedName = nameDraft.trim();

      if (trimmedName === name) {
        setNameDraft(name);
        setNameError(null);
        return;
      }

      if (!CONSTANT_NAME_PATTERN.test(trimmedName)) {
        setNameError(INVALID_NAME_HELPER);
        return;
      }

      if (
        existingNames.some(
          (existingName) =>
            existingName !== name && existingName === trimmedName,
        )
      ) {
        setNameError(DUPLICATE_NAME_HELPER);
        return;
      }

      setNameError(null);
      onRename(name, trimmedName);
    }, [existingNames, isEditable, name, nameDraft, onRename]);

    const handleNameKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") {
          return;
        }

        event.preventDefault();
        commitName();
      },
      [commitName],
    );

    const handleAddValue = useCallback(() => {
      const trimmedValue = valueDraft.trim();

      if (!trimmedValue) {
        return;
      }

      onAddValue(name, trimmedValue);
      setValueDraft("");
    }, [name, onAddValue, valueDraft]);

    const handleValueKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") {
          return;
        }

        event.preventDefault();
        handleAddValue();
      },
      [handleAddValue],
    );

    return (
      <Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={toggle}
          sx={{
            py: 0.75,
            px: 0.5,
            cursor: "pointer",
            borderRadius: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <IconButton
            size="small"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
            aria-label={
              expanded ? `Collapse constant ${name}` : `Expand constant ${name}`
            }
            onClick={(event) => {
              stop(event);
              toggle();
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flexShrink: 1,
                maxWidth: 240,
                fontWeight: 500,
              }}
            >
              {name}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              sx={{
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
                gap: 0.75,
              }}
            >
              {isEditable ? (
                previewValues.length > 0 ? (
                  <>
                    {previewValues.map(({ item, index }) => (
                      <Chip
                        key={`preview-${item}-${index}`}
                        label={item}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      />
                    ))}
                    {overflowCount > 0 && (
                      <Chip
                        label={`+${overflowCount} more`}
                        size="small"
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                      />
                    )}
                  </>
                ) : (
                  <Chip
                    label="No values"
                    size="small"
                    variant="outlined"
                    sx={{
                      flexShrink: 0,
                    }}
                  />
                )
              ) : (
                <Chip
                  label="Unsupported value"
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          </Stack>

          <IconButton
            aria-label={`Delete constant ${name}`}
            onClick={(event) => {
              stop(event);
              onDelete(name);
            }}
            size="small"
            sx={{
              color: (theme) => alpha(theme.palette.error.main, 0.45),
              "&:hover": {
                color: "error.main",
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Collapse in={expanded} unmountOnExit>
          <Stack spacing={1.5} sx={{ pt: 1, pb: 2, pl: 4 }}>
            <TextField
              label="Name"
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              onBlur={commitName}
              onKeyDown={handleNameKeyDown}
              error={!!nameError}
              helperText={
                nameError ??
                (!isEditable ? UNSUPPORTED_CONSTANT_HELPER : undefined)
              }
              size="small"
              fullWidth
              disabled={!isEditable}
              inputProps={{
                "aria-label": `Constant name ${name}`,
              }}
            />

            {isEditable ? (
              <Stack spacing={1}>
                {editableValue.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1.5 }}
                  >
                    No values yet. Add a value.
                  </Typography>
                ) : (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    sx={{ gap: 0.75, ml: 1 }}
                  >
                    {sortedEditableValue.map(({ item, index }) => (
                      <Chip
                        key={`${item}-${index}`}
                        label={item}
                        size="small"
                        onDelete={() => onDeleteValue(name, index)}
                        deleteIcon={
                          <CloseIcon
                            aria-label={`Remove value ${item} from ${name}`}
                            fontSize="small"
                          />
                        }
                      />
                    ))}
                  </Stack>
                )}
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Add value"
                    value={valueDraft}
                    onChange={(event) => setValueDraft(event.target.value)}
                    onKeyDown={handleValueKeyDown}
                    size="small"
                    fullWidth
                    inputProps={{
                      "aria-label": `Add value ${name}`,
                    }}
                  />
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddValue}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Chip
                  label="Preserved — edit in Advanced tab"
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ alignSelf: "flex-start" }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Menlo, Monaco, Consolas, monospace",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {JSON.stringify(value)}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Collapse>
      </Box>
    );
  },
);

export const ConstantsSection: FC<{
  constants: TOnboardingConfig["constants"];
  onConfigChange: AppSettingsTabProps["onConfigChange"];
}> = memo(({ constants, onConfigChange }) => {
  const [expandedConstants, setExpandedConstants] = useState<
    Record<string, boolean>
  >({});
  const entries = useMemo(() => Object.entries(constants ?? {}), [constants]);
  const constantNames = useMemo(() => entries.map(([name]) => name), [entries]);

  const toggleConstant = useCallback((name: string) => {
    setExpandedConstants((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  }, []);

  const handleAddConstant = useCallback(() => {
    onConfigChange((prev) => {
      const currentConstants = (prev.constants ?? {}) as ConstantsMap;
      const nextName = getNextConstantName(currentConstants);

      return buildConfigWithConstants(prev, {
        ...currentConstants,
        [nextName]: [],
      });
    });
  }, [onConfigChange]);

  const handleRenameConstant = useCallback(
    (currentName: string, nextName: string) => {
      const currentConstants = (constants ?? {}) as ConstantsMap;

      if (
        currentName === nextName ||
        !Object.prototype.hasOwnProperty.call(currentConstants, currentName) ||
        Object.prototype.hasOwnProperty.call(currentConstants, nextName)
      ) {
        return;
      }

      setExpandedConstants((prevExpanded) => {
        const nextExpanded = { ...prevExpanded };

        if (prevExpanded[currentName]) {
          nextExpanded[nextName] = true;
        }

        delete nextExpanded[currentName];

        return nextExpanded;
      });

      onConfigChange((prev) => {
        const nextConstants = (prev.constants ?? {}) as ConstantsMap;

        if (
          currentName === nextName ||
          !Object.prototype.hasOwnProperty.call(nextConstants, currentName) ||
          Object.prototype.hasOwnProperty.call(nextConstants, nextName)
        ) {
          return prev;
        }

        return buildConfigWithConstants(
          prev,
          renameConstantKey(nextConstants, currentName, nextName),
        );
      });
    },
    [constants, onConfigChange],
  );

  const handleDeleteConstant = useCallback(
    (name: string) => {
      setExpandedConstants((prev) => {
        if (!prev[name]) {
          return prev;
        }

        const nextExpanded = { ...prev };

        delete nextExpanded[name];

        return nextExpanded;
      });

      onConfigChange((prev) => {
        const currentConstants = (prev.constants ?? {}) as ConstantsMap;

        if (!Object.prototype.hasOwnProperty.call(currentConstants, name)) {
          return prev;
        }

        return buildConfigWithConstants(
          prev,
          Object.fromEntries(
            Object.entries(currentConstants).filter(
              ([currentName]) => currentName !== name,
            ),
          ),
        );
      });
    },
    [onConfigChange],
  );

  const handleAddConstantValue = useCallback(
    (name: string, nextValue: string) => {
      onConfigChange((prev) => {
        const currentConstants = (prev.constants ?? {}) as ConstantsMap;
        const currentValue = currentConstants[name];

        if (!isEditableConstantValue(currentValue)) {
          return prev;
        }

        return buildConfigWithConstants(prev, {
          ...currentConstants,
          [name]: sortConstantValues([...currentValue, nextValue]),
        });
      });
    },
    [onConfigChange],
  );

  const handleDeleteConstantValue = useCallback(
    (name: string, index: number) => {
      onConfigChange((prev) => {
        const currentConstants = (prev.constants ?? {}) as ConstantsMap;
        const currentValue = currentConstants[name];

        if (!isEditableConstantValue(currentValue)) {
          return prev;
        }

        return buildConfigWithConstants(prev, {
          ...currentConstants,
          [name]: sortConstantValues(
            currentValue.filter((_, valueIndex) => valueIndex !== index),
          ),
        });
      });
    },
    [onConfigChange],
  );

  return (
    <Paper
      sx={{
        padding: "20px 32px",
        background: "#ffffff",
        borderRadius: "14px",
        border: "none",
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Constants
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reusable named value lists for JSON conditions that use const
            references.
          </Typography>
        </Stack>

        {entries.length > 0 &&
          entries.map(([name, value]) => (
            <ConstantRow
              key={name}
              expanded={!!expandedConstants[name]}
              existingNames={constantNames}
              name={name}
              value={value}
              onAddValue={handleAddConstantValue}
              onDelete={handleDeleteConstant}
              onDeleteValue={handleDeleteConstantValue}
              onRename={handleRenameConstant}
              onToggle={toggleConstant}
            />
          ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddConstant}
          sx={{ alignSelf: "flex-start" }}
        >
          Add constant
        </Button>
      </Stack>
    </Paper>
  );
});
